import { useTheme } from 'styled-components';

interface LogoProps {
	width?: number;
	height?: number;
	color?: string;
	className?: string;
}

// Aspect ratio of the logo SVG: 105:18
const LOGO_ASPECT_RATIO = 105 / 18;

const Logo = ({ width, height, color, className }: LogoProps) => {
	const theme = useTheme();

	// Calculate dimensions maintaining aspect ratio
	let finalWidth: number;
	let finalHeight: number;

	if (width && height) {
		// Both provided - use as-is (may distort)
		finalWidth = width;
		finalHeight = height;
	} else if (width) {
		// Only width provided - calculate height
		finalWidth = width;
		finalHeight = width / LOGO_ASPECT_RATIO;
	} else if (height) {
		// Only height provided - calculate width
		finalHeight = height;
		finalWidth = height * LOGO_ASPECT_RATIO;
	} else {
		// Default size (maintains aspect ratio)
		finalWidth = 105;
		finalHeight = 18;
	}

	const fillColor = color || theme.gray[1000];

	return (
		<svg
			width={finalWidth}
			height={finalHeight}
			viewBox="0 0 105 18"
			xmlns="http://www.w3.org/2000/svg"
			aria-hidden
			className={className}
			style={{ display: 'block' }}
		>
			<path
				fill={fillColor}
				d="M12.904 4.664C15.352 4.664 17.056 6.536 17.056 8.984V17H14.92V9.704C14.92 8.144 14.032 6.608 12.256 6.608C10.552 6.608 9.592 8.024 9.592 9.704V17H7.504V9.704C7.504 8.144 6.616 6.608 4.816 6.608C3.04 6.608 2.176 8.024 2.176 9.704V17H0.04V4.904H2.176V6.536L3.64 5.072C3.88 4.952 4.432 4.664 5.488 4.664C7.168 4.664 8.488 5.576 9.088 6.944L11.008 5.048C11.32 4.88 11.968 4.664 12.904 4.664ZM25.0971 17.24C21.6891 17.24 18.9291 14.576 18.9291 11.288C18.9291 9.728 19.5051 8.264 20.8011 6.944L22.5771 5.12C22.9611 4.904 23.8011 4.664 24.8811 4.664C28.2651 4.664 31.0251 7.304 31.0251 10.616C31.0251 12.128 30.5211 13.592 29.1051 15.008L27.4011 16.76C26.8251 17.024 26.0571 17.24 25.0971 17.24ZM25.3611 15.272C27.2091 15.272 28.9851 13.784 28.9851 11.288C28.9851 8.624 26.8251 6.608 24.5931 6.608C22.7451 6.608 20.9931 8.096 20.9931 10.592C20.9931 13.256 23.1291 15.272 25.3611 15.272ZM38.3581 17.24C34.9501 17.24 32.1901 14.576 32.1901 11.288C32.1901 9.728 32.7661 8.264 34.0621 6.944L35.8381 5.12C36.2221 4.904 37.0621 4.664 38.1421 4.664C41.5261 4.664 44.2861 7.304 44.2861 10.616C44.2861 12.128 43.7821 13.592 42.3661 15.008L40.6621 16.76C40.0861 17.024 39.3181 17.24 38.3581 17.24ZM38.6221 15.272C40.4701 15.272 42.2461 13.784 42.2461 11.288C42.2461 8.624 40.0861 6.608 37.8541 6.608C36.0061 6.608 34.2541 8.096 34.2541 10.592C34.2541 13.256 36.3901 15.272 38.6221 15.272ZM48.451 17H46.315V4.904H48.451V6.68L50.083 5C50.347 4.88 50.995 4.664 51.907 4.664C54.739 4.664 56.443 6.584 56.443 9.56V17H54.331V10.136C54.331 7.928 53.251 6.608 51.451 6.608C49.675 6.608 48.451 7.928 48.451 10.136V17ZM64.3135 17.24C60.5935 17.24 58.3135 14.48 58.3135 11.432C58.3135 9.656 59.2255 8.168 60.6415 6.704L62.1535 5.168C62.6095 4.904 63.3775 4.664 64.4335 4.664C66.4495 4.664 68.0815 5.648 68.8735 6.656L67.4575 8.168C66.8575 7.424 65.7535 6.728 64.3375 6.728C62.1775 6.728 60.4255 8.6 60.4255 11C60.4255 13.328 62.1535 15.128 64.3855 15.128C66.2815 15.128 67.2415 14.144 67.7695 13.64L69.0655 15.152C68.0335 16.376 66.3535 17.24 64.3135 17.24ZM72.8635 17H70.7275V0.199999H72.8635V6.68L74.4955 5C74.6875 4.904 75.2875 4.664 76.2715 4.664C79.1755 4.664 80.8555 6.584 80.8555 9.56V17H78.7435V10.136C78.7435 7.928 77.6635 6.608 75.8635 6.608C74.0875 6.608 72.8635 7.928 72.8635 10.136V17ZM86.038 3.488H83.254V0.871999H86.038V3.488ZM85.726 17H83.59V4.904H85.726V17ZM90.7135 17H88.5775V0.199999H90.7135V17ZM102.493 6.536V0.175999H104.629V17H102.493V15.08L100.765 16.856C100.357 17.072 99.517 17.24 98.701 17.24C95.341 17.24 92.725 14.576 92.725 11.24C92.725 9.776 93.229 8.288 94.765 6.704L96.301 5.096C96.781 4.856 97.477 4.664 98.437 4.664C100.117 4.664 101.629 5.408 102.493 6.536ZM99.037 15.272C101.005 15.272 102.661 13.784 102.637 11.264C102.613 8.216 100.333 6.608 98.341 6.608C96.397 6.608 94.765 8.096 94.789 10.616C94.813 13.664 97.045 15.272 99.037 15.272Z"
			/>
		</svg>
	);
};

export default Logo;
